import * as React from 'react';
import { Button, Checkbox, Form, Modal, Typography } from 'antd';
import { InputMeta } from '../types';
import { FieldSettings } from '../stores/ProjectApplicationDefinitionEditVisualStore';

type Props = {
    field: InputMeta | null;
    initialValues: FieldSettings;
    isVisible: boolean;
    isDisabled: boolean;
    onCancel: () => void;
    onSubmit: (values: FieldSettings) => void;
};

const ApplicationFieldSettingsModal: React.FC<Props> = ({
    field,
    initialValues,
    isVisible,
    isDisabled,
    onCancel,
    onSubmit
}) => {
    const [form] = Form.useForm<FieldSettings>();
    const [isVisibleOnUi, setIsVisibleOnUi] = React.useState(initialValues.isVisible);

    React.useEffect(() => {
        if (!field) {
            return;
        }

        form.setFieldsValue(initialValues);
        setIsVisibleOnUi(initialValues.isVisible);
    }, [field, form, initialValues]);

    return (
        <Modal
            visible={isVisible}
            title={'Field Settings - ' + field?.name}
            onCancel={onCancel}
            destroyOnClose
            footer={[
                <Button key="field-settings-cancel" className="light" size="large" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="field-settings-save"
                    data-id="field-settings-save"
                    form="field-settings-form"
                    type="primary"
                    htmlType="submit"
                    disabled={isDisabled}
                    size="large"
                >
                    Save
                </Button>
            ]}
        >
            {field ? (
                <Form<FieldSettings>
                    key={field.id}
                    id="field-settings-form"
                    form={form}
                    layout="vertical"
                    initialValues={initialValues}
                    onFinish={values => {
                        if (isDisabled) {
                            return;
                        }

                        const defaultIsRequired = initialValues.defaultIsRequired ?? false;

                        onSubmit({
                            ...initialValues,
                            isVisible: values.isVisible,
                            isRequired: values.isVisible && values.isRequired,
                            requiredOverride:
                                values.isVisible && values.isRequired !== defaultIsRequired
                                    ? values.isRequired
                                    : undefined
                        });
                    }}
                    onValuesChange={changedValues => {
                        if (changedValues.isVisible === false) {
                            form.setFieldsValue({ isRequired: false });
                        }

                        if (typeof changedValues.isVisible === 'boolean') {
                            setIsVisibleOnUi(changedValues.isVisible);
                        }
                    }}
                >
                    <div
                        style={{
                            border: '1px solid #E4E5ED',
                            borderRadius: 4,
                            padding: 16,
                            marginBottom: 16
                        }}
                    >
                        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Settings</div>

                        <Form.Item name="isVisible" valuePropName="checked" style={{ marginBottom: 12 }}>
                            <Checkbox disabled={isDisabled}>Visible on UI</Checkbox>
                        </Form.Item>

                        <Form.Item name="isRequired" valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Checkbox disabled={isDisabled || isVisibleOnUi === false}>Required field</Checkbox>
                        </Form.Item>
                    </div>

                    <div
                        style={{
                            border: '1px solid #E4E5ED',
                            borderRadius: 4,
                            padding: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 12,
                                gap: 12
                            }}
                        >
                            <div style={{ fontSize: 14, fontWeight: 500 }}>Validation rules</div>

                            <Button disabled>Add Validation Rule</Button>
                        </div>

                        <Typography.Text type="secondary">There are no existing rules yet.</Typography.Text>
                    </div>
                </Form>
            ) : null}
        </Modal>
    );
};

export default ApplicationFieldSettingsModal;
